import { useState, useRef, useEffect } from 'react';
import { scaleLinear, scaleOrdinal, scaleThreshold } from '@visx/scale';
import type { ScaleThreshold } from 'd3-scale';
import Label from '../Label';
import Axis from '../Axis';
import Valuation from '../Valuation';
import { normalisation } from '@utilities/vector';
import useWindowSize from '@/hooks/useWindowSize';
import { useTheme } from '@simplywallst/ui-core';
import { useServerEnv, Size } from '@/hooks/useServerEnv';
import { useChartDefinitionsContext } from '@/components/ChartDefinitionsContext';

const keys = ['Share Price', 'Intrinsic Value'];
const bandingKeys = ['20% Undervalued', 'About Right', '20% Overvalued'];
const bandingKeysReversed = bandingKeys.slice().reverse();

const normalise = (intrinsicValue, sharePrice) => {
  const normalisedValues = normalisation([intrinsicValue, sharePrice]);
  const normalisedIntrinsicValue = normalisedValues[0];
  const normalisedSharePrice = normalisedValues[1];
  return {
    [keys[0]]: normalisedSharePrice < 0 ? 0 : normalisedSharePrice,
    [keys[1]]: normalisedIntrinsicValue < 0 ? 0 : normalisedIntrinsicValue,
  };
};

interface Props {
  intrinsicDiscount: number;
  intrinsicDiscountFormatted: string;
  intrinsicValue: number;
  intrinsicValueFormatted: string;
  sharePrice: number;
  sharePriceFormatted: string;
  showNewStyle?: boolean;
  showEmptyState?: boolean;
  showAxis?: boolean;
  labels?: {
    currentValue: string;
    fairValue: string;
    undervalued: string;
    overvalued: string;
    axisAboutRight: string;
    axisOvervalued: string;
    axisUndervalued: string;
  };
}

const DCF = ({
  intrinsicDiscount,
  intrinsicValue,
  sharePrice,
  sharePriceFormatted,
  intrinsicValueFormatted,
  intrinsicDiscountFormatted,
  showNewStyle,
  showEmptyState,
  showAxis = true,
  labels = {
    currentValue: 'Current Price',
    fairValue: 'Fair Value',
    undervalued: 'Undervalued',
    overvalued: 'Overvalued',
    axisAboutRight: 'About Right',
    axisOvervalued: '20% Overvalued',
    axisUndervalued: '20% Undervalued',
  },
}: Props) => {
  const chartHeight = 240;
  const barHeight = 72;
  const barPadding = 24;
  const bandingHeight = 216;
  const valuationPaddingBreakPoints = {
    desktop: 43,
    mobile: 41,
  };
  const axisPaddingBreakPoints = {
    desktop: 90,
    mobile: 85,
  };

  const breakPointMapper = {
    [Size.SM]: 'mobile',
    [Size.LG]: 'desktop',
  };
  const serverEnv = useServerEnv();

  const { chartFailPattern } = useChartDefinitionsContext();
  const containerRef = useRef<SVGSVGElement>(null); // https://developer.mozilla.org/en-US/docs/Web/API/SVGSVGElement
  const axisRef = useRef<SVGSVGElement>(null);
  const [bound, setBound] = useState<number>(0);
  const [valuationPadding, setValuationPadding] = useState<number>(
    serverEnv.isServer
      ? valuationPaddingBreakPoints[breakPointMapper[serverEnv.size]]
      : 0
  );
  const [axisPadding, setAxisPadding] = useState<number>(
    serverEnv.isServer
      ? axisPaddingBreakPoints[breakPointMapper[serverEnv.size]]
      : 0
  );

  const size = useWindowSize();
  const theme = useTheme();

  const data = normalise(intrinsicValue, sharePrice);

  const xScale = scaleLinear({
    domain: [-0.1, 1.3],
  });

  const IV = data[keys[1]];

  const intrinsicValueX = xScale(IV) as number;
  const intrinsicValueXMin = xScale(IV - IV * 0.2) as number;
  const intrinsicValueXMax = xScale(IV + IV * 0.2) as number;

  const sharePriceX = xScale(data[keys[0]]) as number;

  const bandingColors: string[] = [
    theme.color.good,
    theme.color.neutral,
    theme.color.bad,
  ];

  const bandingColor = scaleOrdinal({
    domain: bandingKeys,
    range: bandingColors,
  });

  const bandingScale = scaleOrdinal({
    domain: bandingKeys,
    range: [intrinsicValueXMin, intrinsicValueXMax, 1],
  });

  const axisScale = scaleOrdinal({
    domain: bandingKeys,
    range: [intrinsicValueXMin, intrinsicValueX, intrinsicValueXMax],
  });

  const colorThreshold = scaleThreshold({
    domain: [0, intrinsicValueXMin, intrinsicValueXMax, 1],
    range: ['white', ...bandingColors],
  }) as ScaleThreshold<number, string>;

  const min = Math.min(intrinsicValueX, sharePriceX);
  const diff = Math.abs(sharePriceX - intrinsicValueX);

  useEffect(() => {
    const { current: container } = containerRef;
    if (container === null) return;
    const containerWidth = container.getBBox().width;
    setBound(containerWidth);
  }, [intrinsicValue, sharePrice, size]);

  useEffect(() => {
    if (size.width !== undefined && size.width < theme.mdMinRaw) {
      valuationPadding !== valuationPaddingBreakPoints.mobile &&
        setValuationPadding(valuationPaddingBreakPoints.mobile);
    } else {
      valuationPadding !== valuationPaddingBreakPoints.desktop &&
        setValuationPadding(valuationPaddingBreakPoints.desktop);
    }
  }, [
    size,
    theme.mdMinRaw,
    valuationPadding,
    valuationPaddingBreakPoints.desktop,
    valuationPaddingBreakPoints.mobile,
  ]);

  useEffect(() => {
    const { current: axis } = axisRef;
    if (axis) {
      let axisHeight = axisPadding;
      if (size.width !== undefined) {
        if (
          size.width < theme.mdMinRaw &&
          axisPadding !== axisPaddingBreakPoints.mobile
        ) {
          axisHeight = axisPaddingBreakPoints.mobile;
        }
        if (
          size.width > theme.mdMinRaw &&
          axisPadding !== axisPaddingBreakPoints.desktop
        ) {
          axisHeight = axisPaddingBreakPoints.desktop;
        }
      }
      const labelDiff = Math.max(
        ...bandingKeysReversed.map((key, i, a) =>
          a[i + 1]
            ? (axisScale(key) as number) - (axisScale(a[i + 1]) as number)
            : 0
        )
      );
      setAxisPadding(labelDiff * bound < 26 ? 0 : axisHeight);
    }
  }, [
    intrinsicValue,
    sharePrice,
    bound,
    axisScale,
    size,
    axisPadding,
    theme.mdMinRaw,
    axisPaddingBreakPoints.desktop,
    axisPaddingBreakPoints.mobile,
  ]);

  const bandingKeyLabels = [
    labels.axisUndervalued,
    labels.axisAboutRight,
    labels.axisOvervalued,
  ];

  return (
    <svg
      width="100%"
      height={valuationPadding + chartHeight + axisPadding}
      shapeRendering="crispEdges"
    >
      {!showEmptyState && (
        <Valuation
          color={colorThreshold(sharePriceX) || '#333'}
          title={intrinsicDiscount > 0 ? labels.undervalued : labels.overvalued}
          value={intrinsicDiscountFormatted}
          x={min}
        />
      )}
      <svg
        width="100%"
        height={chartHeight}
        y={valuationPadding}
        ref={containerRef}
      >
        <rect
          x="0"
          y={barPadding}
          width="100%"
          height="216"
          fill={showNewStyle ? chartFailPattern : '#E64141'}
        />
        <defs>
          <linearGradient id="BarGradient">
            <stop offset="0%" stopColor="rgb(21, 27, 36)" stopOpacity="0.5" />
            <stop offset="100%" stopColor="rgb(21, 27, 36)" stopOpacity="0.8" />
          </linearGradient>
        </defs>
        {bandingKeysReversed.map((key, i) => {
          if (key !== '20% Overvalued' || !showNewStyle)
            return (
              <rect
                key={`banding-${i}`}
                x="0"
                y={barPadding}
                width={`${bandingScale(key) * 100}%`}
                height={bandingHeight}
                fill={bandingColor(key)}
              />
            );
          else return null;
        })}
        <g transform={`translate(0,${barPadding})`}>
          {keys.map((key, i) => {
            const xScaleLinear = xScale(data[key]) as number;
            const xPos = `${Number(xScaleLinear) * 100}%`;
            const yPos = barPadding + i * barHeight + i * barPadding;

            if (showEmptyState && key === 'Share Price') return null;

            return (
              <g key={`barstack-horizontal-${i}`}>
                <rect
                  x="0"
                  y={showEmptyState ? `calc(50% - ${barHeight / 2}px)` : yPos}
                  width={xPos}
                  height={barHeight}
                  fill={
                    i === 0 && showNewStyle
                      ? theme.color.noir
                      : 'url(#BarGradient)'
                  }
                />
                <line
                  strokeWidth={showNewStyle ? 2 : 1}
                  x1={xPos}
                  x2={xPos}
                  y1={showEmptyState ? `calc(50% - ${barHeight / 2}px)` : yPos}
                  y2={
                    showEmptyState
                      ? `calc(50% - ${barHeight / 2}px + ${barHeight}px)`
                      : yPos + barHeight
                  }
                  stroke={
                    i === 0 && showNewStyle ? theme.color.brand01 : 'white'
                  }
                />
              </g>
            );
          })}
        </g>
        {!showEmptyState &&
          keys.map((key, i) => {
            const xScaleLinear = xScale(data[key]) as number;
            const xPos = `${Number(xScaleLinear) * 100}%`;
            return (
              <line
                key={`marker-${i}`}
                x1={xPos}
                x2={xPos}
                y1="0"
                y2={
                  barPadding * 2 +
                  barHeight +
                  i * barHeight +
                  i * barPadding +
                  i * barPadding
                }
                stroke="white"
                strokeOpacity="0.3"
              />
            );
          })}
        {!showEmptyState && (
          <rect
            x={`${min * 100}%`}
            y="0"
            width={`${diff * 100}%`}
            height="2"
            fill={colorThreshold(sharePriceX)}
          />
        )}
        {!showEmptyState && (
          <Label
            bound={bound}
            height={barHeight}
            x={sharePriceX}
            y={barPadding * 2}
            label={labels.currentValue}
            value={sharePriceFormatted}
          />
        )}
        <Label
          bound={bound}
          height={barHeight}
          x={intrinsicValueX}
          y={
            showEmptyState
              ? `calc(50% - ${barHeight / 2}px + ${barPadding}px)`
              : barPadding * 3 + barHeight
          }
          label={labels.fairValue}
          value={showEmptyState ? 'N/A' : intrinsicValueFormatted}
        />
      </svg>
      {showAxis && (
        <Axis
          ref={axisRef}
          color={bandingColor}
          keys={bandingKeys}
          keyLabels={bandingKeyLabels}
          xScale={axisScale}
          y={valuationPadding + chartHeight}
          showEmptyState={showEmptyState}
        />
      )}
    </svg>
  );
};

export default DCF;
